import {AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import moment from 'moment';
import {UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {SnackBarComponent} from '../../snackbar/snackbar.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SessionService} from '../../../services/session/session.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {Dictionary} from 'highcharts/highcharts.src';
import {UsersService} from '../../../services/users/users.service';
import {
  TeamCompanyUsersService,
  UserCompanyAssociation,
} from '../../../services/teamcompanyusers/teamcompanyusers.service';

type CompanyAssociationsLoadState = 'idle' | 'loading' | 'loaded' | 'error';

@Component({
    selector: 'app-adduserdialog',
    templateUrl: './adduserdialog.component.html',
    styleUrls: ['./adduserdialog.component.scss', '../../../directives/mat-basic-spinner.directive.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdduserdialogComponent implements OnInit, AfterViewInit, OnDestroy {

  public maxDate = moment().add(-18, 'year').toDate();
  public form;

  private oldTel = '';
  private currentEmail = '';
  public phoneVerified = false;
  public emailVerified = false;
  public partners = Array<Dictionary<any>>();
  public user;
  public editMode = false;
  public saving = false;
  private oldPhone;
  public hideSsn = true;
  public companyAssociations: UserCompanyAssociation[] = [];
  public companyAssociationsLoadState: CompanyAssociationsLoadState = 'idle';
  private companyAssociationsRequestGeneration = 0;
  private destroyed = false;
  public readonly companyAssociationsCopy = {
    empty: 'No company associations',
    error: 'Unable to load company associations.',
    loading: 'Loading company associations...',
    noDirectRoles: 'No direct company roles',
    noTeamAssociations: 'No Team associations',
  };

  constructor(
      private formBuilder: UntypedFormBuilder,
      public dialogRef: MatDialogRef<AdduserdialogComponent>,
      @Inject(MAT_DIALOG_DATA) public data: AdduserDialogModel,
      private sessionService: SessionService,
      private snackBar: MatSnackBar,
      private uiAlertService: UiAlertService,
      private usersService: UsersService,
      private teamCompanyUsersService: TeamCompanyUsersService
  ) {
    this.user = data.user;
    this.editMode = !!data.user;
    this.emailVerified = this.user?.isVerifiedEmail;
    this.phoneVerified = this.isUserPhoneVerified(this.user);
    this.oldPhone = this.user?.phoneNumber?.substr(1);
    this.form = this.formBuilder.group({
      firstname: new UntypedFormControl(this.user?.firstName, Validators.required),
      lastname: new UntypedFormControl(this.user?.lastName, Validators.required),
      dob: new UntypedFormControl(this.user ? moment(this.user.birthdate, 'YYYY-MM-DD').toDate() : undefined),
      tel: new UntypedFormControl(this.oldPhone, Validators.pattern('^[0-9]{10}$')),
      email: new UntypedFormControl(this.user?.email, [Validators.email, Validators.required]),
      alternateEmail: new UntypedFormControl(this.user?.accountRecovery?.alternateEmail, Validators.email),
      address1: new UntypedFormControl(this.user?.address1),
      address2: new UntypedFormControl(this.user?.address2),
      city: new UntypedFormControl(this.user?.city),
      state: new UntypedFormControl(this.user?.state),
      postalCode: new UntypedFormControl(this.user?.postalCode),
      ssn: new UntypedFormControl(this.user?.ssn, Validators.pattern('^[0-9]{9}$')),
      partner: new UntypedFormControl(this.user?.partnerId, Validators.required),
      receiveOffers: new UntypedFormControl(this.getReceiveOffersValue(), Validators.required),
    });
  }

  ngOnInit(): void {
    this.usersService.getVisibleBrands().then(partners => {
      this.partners = partners;
    });
    if (this.user?.id) {
      this.form.get('email')?.disable({ emitEvent: false });
      this.loadCompanyAssociations();
    }
  }

  ngAfterViewInit(): void {
    this.form.get('email')?.valueChanges.subscribe(value => {
      this.emailVerified = false;
    });
    this.form.get('tel')?.valueChanges.subscribe(value => {
      this.phoneVerified = false;
    });
    this.form.get('tel')?.setValue(this.user?.phoneNumber?.substr(1), {emitEvent: false});
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.companyAssociationsRequestGeneration += 1;
  }

  public get canViewProducts(): boolean {
    return !!this.user?.id && this.sessionService.isExactSuperAdmin();
  }

  public isUserPhoneVerified(user: any): boolean {
    return user?.isConfirmed === true || user?.isConfirmed === 1 || user?.isConfirmed === '1';
  }

  public isEmailVerificationDisabled(): boolean {
    return !`${this.user?.email || this.form.get('email')?.value || ''}`.trim();
  }

  private loadCompanyAssociations(): void {
    const requestedUserId = this.user.id;
    const requestGeneration = ++this.companyAssociationsRequestGeneration;
    this.companyAssociationsLoadState = 'loading';
    this.teamCompanyUsersService.getCompanyAssociationsForUser(requestedUserId).then((response: any) => {
      if (!this.isCurrentCompanyAssociationsRequest(requestGeneration, requestedUserId)) {
        return;
      }
      const plainResponse = typeof response?.plain === 'function' ? response.plain() : response;
      const associations = Array.isArray(plainResponse?.associations) ? plainResponse.associations : [];
      this.companyAssociations = associations
        .map((association: any) => this.mapCompanyAssociation(association))
        .filter((association: UserCompanyAssociation | null): association is UserCompanyAssociation => !!association);
      this.companyAssociationsLoadState = 'loaded';
    }).catch((error: any) => {
      if (!this.isCurrentCompanyAssociationsRequest(requestGeneration, requestedUserId)) {
        return;
      }
      console.error('LOAD COMPANY ASSOCIATIONS ERROR', error);
      this.companyAssociations = [];
      this.companyAssociationsLoadState = 'error';
    });
  }

  private isCurrentCompanyAssociationsRequest(requestGeneration: number, requestedUserId: string): boolean {
    return !this.destroyed
      && requestGeneration === this.companyAssociationsRequestGeneration
      && this.user?.id === requestedUserId;
  }

  private mapCompanyAssociation(association: any): UserCompanyAssociation | null {
    const companyId = typeof association?.companyId === 'string' ? association.companyId.trim() : '';
    const companyName = typeof association?.companyName === 'string' ? association.companyName.trim() : '';
    if (!companyId || !companyName) {
      return null;
    }

    return {
      companyId,
      companyName,
      directRoles: Array.isArray(association.directRoles)
        ? association.directRoles.filter((role: any) => typeof role === 'string' && role.trim()).map((role: string) => role.trim())
        : [],
      teamAssociations: Array.isArray(association.teamAssociations)
        ? association.teamAssociations
          .filter((team: any) => typeof team?.teamId === 'string' && team.teamId.trim()
            && typeof team?.teamName === 'string' && team.teamName.trim())
          .map((team: any) => ({ teamId: team.teamId.trim(), teamName: team.teamName.trim() }))
        : [],
    };
  }

  public trackCompanyAssociation(_index: number, association: UserCompanyAssociation): string {
    return association.companyId;
  }

  private fixNumber(thisPhone: string): string {
    return thisPhone ? '1' + thisPhone : '';
  }

  private saveAlternateEmail(userId: string): Promise<any> {
    const email = `${this.form.get('alternateEmail')?.value || ''}`.trim().toLowerCase();
    const currentEmail = `${this.user?.accountRecovery?.alternateEmail || ''}`.trim().toLowerCase();
    if (email === currentEmail) {
      return Promise.resolve();
    }
    return email
      ? this.usersService.setAlternateEmail(userId, email)
      : this.usersService.removeAlternateEmail(userId);
  }


  saveClicked(event: any): void {
    event.preventDefault();
    if (this.form.errors || this.form.get('alternateEmail')?.invalid) {
      console.log('SAVECLICKED', this.form.errors);
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Missing or invalid field(s)'});
      return;
    }
    const partnerId = this.form.get('partner')?.value;
    const data: any = {
      firstName: this.form.get('firstname')?.value,
      lastName: this.form.get('lastname')?.value,
      nickname: this.form.get('nickname')?.value,
      birthdate: this.form.get('dob')?.value,
      email: undefined,
      password: '',
      username: this.form.get('email')?.value,
      address1: this.form.get('address1')?.value,
      address2: this.form.get('address2')?.value,
      city: this.form.get('city')?.value,
      state: this.form.get('state')?.value,
      postalCode: this.form.get('postalCode')?.value,
      ssn: this.form.get('ssn')?.value,
      signupUserId: this.usersService.getCurrentUserId(),
    };
    if (!this.user) {
      data.email = this.form.get('email')?.value;
      data.offers = this.form.get('receiveOffers')?.value === 1;
    }
    this.saving = true;
    const phone = this.form.get('tel')?.value;
    if (this.user) {
      this.usersService.updateUser(this.user.id, data).then(() => {
        this.usersService.updateUserBrand(this.user.id, partnerId).then(() => {
          const allDone = () => {
            this.saveAlternateEmail(this.user.id).then(() => {
              this.snackBar.openFromComponent(SnackBarComponent, {data: `User updated`});
              this.saving = false;
              this.dialogRef.close({...data, partnerId});
            }).catch(error => {
              console.log('UPDATE BACKUP EMAIL ERROR', error);
              const detail = typeof error?.data?.error === 'string' ? ` ${error.data.error}` : '';
              this.snackBar.openFromComponent(SnackBarComponent, {
                data: `Other profile changes were saved, but the backup email update could not be completed.${detail}`,
              });
              this.saving = false;
            });
          };
          const updateReceiveOffers = () => {
            this.usersService.updateReceiveOffersSetting(this.user.id, this.form.get('receiveOffers')?.value).then(() => {
              allDone();
            }).catch(error => {
              console.log('UPDATE RECEIVE OFFERS ERROR', error);
              this.snackBar.openFromComponent(SnackBarComponent, {data: 'Error updating receive offers setting'});
              this.saving = false;
            });
          };
          if (this.oldPhone === phone) {
            updateReceiveOffers();
          } else if (phone) {
            this.usersService.setPhoneNumberByUserId(this.user.id, this.fixNumber(phone)).then(() => {
              updateReceiveOffers();
            }).catch(error => {
              this.snackBar.openFromComponent(SnackBarComponent, {data: error.data?.error});
              this.saving = false;
            });
          } else if (this.oldPhone) {
            this.usersService.removePhoneNumberByUserId(this.user.id, this.fixNumber(this.oldPhone)).then(() => {
              updateReceiveOffers();
            }).catch(error => {
              this.snackBar.openFromComponent(SnackBarComponent, {data: 'Error removing phone number'});
              this.saving = false;
            });
          } else {
            updateReceiveOffers();
          }
        });
      }).catch((error: any) => {
        console.log('UPDATE ERROR', error);
        this.saving = false;
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'Error updating user' });
      });
    } else {
      this.sessionService.getRandomPassword().then(password => {
        data.password = password;
        data.partnerId = partnerId;
        this.sessionService.createAccount(data).then((result: any) => {
          if (phone) {
            this.usersService.setPhoneNumberByUserId(result.userId, this.fixNumber(phone));
          }
          this.usersService.updateUserBrand(result.userId, partnerId).then(() => this.saveAlternateEmail(result.userId)).then(() => {
            this.snackBar.openFromComponent(SnackBarComponent, {data: `User created. Temporary password is ${data.password}`});
            this.saving = false;
            this.uiAlertService.presentAlertConfirm('Now that you have added the user, do you want to verify either their phone number or email now?').then(ok => {
              if (ok) {
                this.user = data;
                this.user.id = result.userId;
              } else {
                this.dialogRef.close(data);
              }
            });
         }).catch((error: any) => {
           console.log('CREATE USER DETAILS ERROR', error);
           this.saving = false;
           this.snackBar.openFromComponent(SnackBarComponent, {data: 'User created, but backup email or brand could not be saved'});
         });
        }).catch((error: any) => {
          console.log('CREATE ERROR', error);
          this.saving = false;
          this.snackBar.openFromComponent(SnackBarComponent, {data: error.status === 409 ? 'Email address already exists. Email addresses must be unique.' : error});
        });
      });
    }
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  private getReceiveOffersValue(): 0 | 1 {
    const receiveOffers = this.user?.receiveOffers ?? this.user?.notificationSettings?.receiveOffers;
    return receiveOffers === 1 || receiveOffers === true ? 1 : 0;
  }

  verifyPhoneNumber(): void {
    const phone = this.form.get('tel')?.value;
    this.usersService.resendPhoneValidation(this.user.id, this.fixNumber(phone)).then(() => {
      this.uiAlertService.presentFieldInput(`Enter phone verification code sent to customer's phone`, 'Enter Code').then(value => {
        if (value) {
          this.usersService.validatePhoneCode(this.user.id, this.fixNumber(phone), value).then(result => {
            console.log('VALIDATION RETURN', result);
            this.snackBar.openFromComponent(SnackBarComponent, {data: result ? 'Phone number verified' : 'Incorrect validation code'});
            this.phoneVerified = !!result;
          }).catch (error => {
            this.snackBar.openFromComponent(SnackBarComponent, {data: 'Incorrect validation code'});
            this.usersService.removePhoneNumber(phone);
          });
        }
      });
    }).catch(error => {
      console.log('VERIFY ERROR', error)
      this.snackBar.openFromComponent(SnackBarComponent, {data: error.error.error});
    });
  }

  verifyEmail(): void {
    this.usersService.resendEmailValidation(this.user.id).then(() => {
      this.uiAlertService.presentFieldInput(`Enter verification code sent to customer's email`, 'Enter Code').then(value => {
        if (value) {
          this.usersService.validateEmailCode(this.user.id, value).then(result => {
            console.log('VALIDATION RETURN', result);
            this.snackBar.openFromComponent(SnackBarComponent, {data: result ? 'Email address verified' : 'Incorrect validation code'});
            this.emailVerified = !!result;
          });
        }
      });
    });
  }

  forceVerifyPhoneNumber(): void {
    this.uiAlertService.presentAlertConfirm('Are you sure you want to force verify this customer\'s phone number. Only do this if you are sure that the number you are supplying is associated with the customer.').then(ok => {
      if (ok) {
        this.usersService.confirmPhoneNumber(this.user.id, this.fixNumber(this.form.get('tel')?.value)).then(() => {
          return this.usersService.updateReceiveSmsSetting(this.user.id, 1);
        }).then(() => {
          this.phoneVerified = true;
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Phone number has been force verified and SMS enabled.'});
        }).catch(error => {
          console.log('FORCE VERIFY PHONE ERROR', error);
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Error force verifying phone number'});
        });
      }
    });
  }

  forceVerifyEmail(): void {
    this.uiAlertService.presentAlertConfirm('Are you sure you want to force verify this customer\'s email address. Only do this if you are sure that the email address you are supplying is associated with the customer.').then(ok => {
      if (ok) {
        this.usersService.verifyEmail(this.user.id).then(() => {
          this.emailVerified = true;
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Email address has been force verified.'});
        }).catch(error => {
          console.log('FORCE VERIFY EMAIL ERROR', error);
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Error force verifying email address'});
        });
      }
    });
  }

  playHelpMovie(): void {
    this.uiAlertService.presentMovie('Modify/Add Customers' , UiAlertService.content.MODIFY_ADD_CUSTOMERS);
  }

}

export class AdduserDialogModel {
  constructor(public user: any) {
  }
}
